/**
 * Terminal result rendering — the deliverable is the hero. Two file sources:
 *  - LIVE stream (`final` envelope): base64 → Blob → object URL (revoked on unmount).
 *  - PERSISTED result (history / reattach): a same-origin download `url` served
 *    by the BFF from its result store — no bytes in the page until clicked.
 *
 * Design intent (customer portal, non-technical audience):
 *  - A clear outcome headline + an at-a-glance summary strip (duration,
 *    questions answered, specialists, sources) instead of engineering detail.
 *  - Files as first-class download cards with a human file-kind label — the
 *    thing the customer came for.
 *  - `partial_success` and `failure` map canonical issue codes to plain
 *    language; the code itself stays visible as a small chip for support.
 */
import { type FlowIssue, fromBase64, Icon, Notice } from '@evt/flow-stream-ui'
import { useEffect, useMemo, useState } from 'react'
import { type Messages, useI18n } from '../i18n'
import { codeMessage } from '../i18n/errors'
import { RunRating } from './RunRating'
import { ratingTokenIn } from './rating-link'

interface ReplyShape {
  subject?: string
  message?: string
}
interface FileShape {
  name: string
  mimetype: string
  /** Present on live final envelopes. */
  contentBase64?: string
  /** Present on persisted results (BFF download route). */
  url?: string
  size?: number
}
interface ResponseShape {
  status: string
  reply?: ReplyShape
  files?: FileShape[]
  issues?: FlowIssue[]
}

/** At-a-glance run facts for the summary strip (all optional — render what exists). */
export interface ResultMeta {
  completedAt?: string
  durationMs?: number | null
  questions?: number
  specialists?: number
  sources?: number
}

interface FileView {
  name: string
  mimetype: string
  size: number
  url: string
  isImage: boolean
  /** Object URLs must be revoked; server URLs must not be. */
  revoke: boolean
}

export function RunResult({
  response,
  meta,
  executionId
}: {
  response: ResponseShape
  meta?: ResultMeta
  /** The run's id: only a rating link minted for it is offered, never a quoted answer's. */
  executionId?: string
}) {
  const { t } = useI18n()
  const m = t.run
  const isError = response.status === 'failure'
  const isPartial = response.status === 'partial_success'

  const files = useMemo<FileView[]>(() => {
    const out: FileView[] = []
    for (const f of response.files ?? []) {
      if (typeof f.contentBase64 === 'string') {
        const bytes = fromBase64(f.contentBase64)
        out.push({
          name: f.name,
          mimetype: f.mimetype,
          size: bytes.length,
          url: URL.createObjectURL(new Blob([bytes], { type: f.mimetype })),
          isImage: f.mimetype.startsWith('image/'),
          revoke: true
        })
      } else if (typeof f.url === 'string') {
        out.push({
          name: f.name,
          mimetype: f.mimetype,
          size: f.size ?? 0,
          url: f.url,
          isImage: f.mimetype.startsWith('image/'),
          revoke: false
        })
      }
    }
    return out
  }, [response.files])

  useEffect(() => {
    return () => {
      for (const f of files) {
        if (f.revoke) URL.revokeObjectURL(f.url)
      }
    }
  }, [files])

  if (isError) {
    const issue = response.issues?.[0]
    return (
      <section className="run-result card is-failure" aria-label={m.outcomeAria}>
        <div className="result-outcome">
          <span className="result-glyph is-failure" aria-hidden="true">
            <Icon name="x" size={20} />
          </span>
          <div>
            <h2 className="result-headline">{m.errorHeadline}</h2>
            <p className="result-sub">
              {sentence(issue ? friendlyIssue(issue, t) : m.errorFallbackCause)} {m.errorNoDeliverable}
            </p>
            {issue && (
              <p className="result-support muted">
                {m.supportMention} <span className="chip mono">{issue.code}</span>
              </p>
            )}
          </div>
        </div>
      </section>
    )
  }

  const reply = response.reply
  // One line per distinct sentence: three files that could not be retrieved are one thing to say, not three.
  const partialNotes = isPartial ? [...new Set((response.issues ?? []).map((issue) => friendlyIssue(issue, t)))] : []
  // The stars inside the sandboxed preview are inert by design; offer the same rating natively.
  const ratingToken = ratingTokenIn(reply?.message, executionId)

  return (
    <section className="run-result card" aria-label={m.deliveryAria}>
      <div className="result-outcome">
        <span className={`result-glyph ${isPartial ? 'is-partial' : 'is-success'}`} aria-hidden="true">
          <Icon name="check" size={20} />
        </span>
        <div>
          <h2 className="result-headline">{isPartial ? m.deliveredWithNotes : m.deliveryReady}</h2>
          <SummaryStrip meta={meta} filesCount={files.length} />
        </div>
      </div>

      {partialNotes.length > 0 && (
        <Notice kind="warning">
          {partialNotes.map((note) => (
            <div key={note}>{note}</div>
          ))}
        </Notice>
      )}

      {files.length > 0 && (
        <div className="result-files">
          <div className="result-section-label">{files.length === 1 ? m.yourFile : m.yourFilesCount(files.length)}</div>
          <div className="result-files-grid">
            {files.map((f) => (
              <FileCard key={f.url} file={f} />
            ))}
          </div>
        </div>
      )}

      {(reply?.subject || reply?.message) && (
        <div className="result-briefing-block">
          <div className="result-section-label">{m.summaryOfWork}</div>
          {reply?.subject && <h3 className="run-result-subject">{reply.subject}</h3>}
          {reply?.message && <ReplyMessage message={reply.message} />}
        </div>
      )}
      {ratingToken && <RunRating token={ratingToken} />}
      {!reply?.subject && !reply?.message && files.length === 0 && <p className="muted">{m.finishedEmpty}</p>}
    </section>
  )
}

/** Compact facts row under the headline — only renders what it knows. */
function SummaryStrip({ meta, filesCount }: { meta?: ResultMeta; filesCount: number }) {
  const { t, formatAgo } = useI18n()
  const m = t.run
  const facts: string[] = []
  if (meta?.completedAt) facts.push(m.finishedAgo(formatAgo(meta.completedAt)))
  if (meta?.durationMs != null && meta.durationMs > 0) facts.push(m.took(friendlyDuration(meta.durationMs, m)))
  if (filesCount > 0) facts.push(m.filesCount(filesCount))
  if (meta?.questions) facts.push(m.questionsAnswered(meta.questions))
  if (meta?.specialists) facts.push(m.specialistsInvolved(meta.specialists))
  if (meta?.sources) facts.push(m.referenceSources(meta.sources))
  if (facts.length === 0) return null
  return <p className="result-sub">{facts.join(' · ')}</p>
}

function FileCard({ file }: { file: FileView }) {
  const { t, formatBytes } = useI18n()
  const m = t.run
  const kind = fileKind(file.mimetype, file.name, m)
  return (
    <a
      className="result-file"
      href={file.url}
      download={file.name}
      title={m.downloadFileTitle(file.name, formatBytes(file.size))}
    >
      {file.isImage ? (
        <span className="result-file-thumb">
          <img src={file.url} alt="" loading="lazy" />
        </span>
      ) : (
        <span className={`result-file-tile ftype-${kind.tone}`} aria-hidden="true">
          {kind.badge}
        </span>
      )}
      <span className="result-file-info">
        <span className="result-file-name">{file.name}</span>
        <span className="result-file-meta muted">
          {kind.label}
          {file.size > 0 && <> · {formatBytes(file.size)}</>}
        </span>
      </span>
      <span className="result-file-dl" aria-hidden="true">
        <Icon name="download" size={15} />
      </span>
    </a>
  )
}

/** Engines reply with a full HTML briefing (the suppressed email body), not markdown. */
function looksLikeHtmlDocument(message: string): boolean {
  const head = message.slice(0, 512).trimStart().toLowerCase()
  return head.startsWith('<!doctype') || head.startsWith('<html') || head.includes('<body')
}

/**
 * Render the reply message. HTML briefings go into a fully sandboxed iframe
 * (`sandbox=""` — no scripts, no same-origin, inert links) with an expand
 * toggle; plain-text/markdown replies keep the simple text rendering.
 */
function ReplyMessage({ message }: { message: string }) {
  const { t } = useI18n()
  const [tall, setTall] = useState(false)
  if (looksLikeHtmlDocument(message)) {
    return (
      <div className="result-briefing-wrap">
        <iframe
          className={`run-result-briefing${tall ? ' is-tall' : ''}`}
          title={t.run.resultBriefingTitle}
          sandbox=""
          srcDoc={message}
          referrerPolicy="no-referrer"
        />
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTall((v) => !v)}>
          <Icon name={tall ? 'chevron' : 'chevron-down'} size={13} /> {tall ? t.run.showLess : t.run.expandBriefing}
        </button>
      </div>
    )
  }
  return <div className="run-result-message markdown-body">{message}</div>
}

/**
 * Canonical FlowIssue codes → customer language: the run outcomes below, then
 * the error catalog every refusal is worded from (`codeMessage` — the file
 * codes, fr-docs' and the BFF's). Unknown codes fall back to the canonical
 * message, which is written for an operator — so any code whose message
 * carries internals (tool names, config knobs, provider file ids, hosts)
 * MUST be mapped rather than left to the fallback.
 *
 * Exported for `RunResult.test.ts`, which pins exactly that: the mapped codes
 * never leak their raw message to a customer.
 */
export function friendlyIssue(issue: FlowIssue, t: Messages): string {
  const m = t.run
  switch (issue.code) {
    case 'payload_too_large':
      return m.issues.payloadTooLarge
    case 'engine_timeout':
      return m.issues.engineTimeout
    case 'engine_caller_hangup':
      return m.issues.engineCallerHangup
    case 'engine_provider_stream_drop':
      return m.issues.engineProviderStreamDrop
    case 'engine_tool_loop':
      return m.issues.engineToolLoop
    case 'user_cancelled':
      return m.issues.userCancelled
    default:
      return codeMessage(t, issue.code) ?? issue.message
  }
}

/** Close a clause with a period so joined sentences read cleanly. */
function sentence(s: string): string {
  const trimmed = s.trim()
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`
}

function friendlyDuration(ms: number, m: Messages['run']): string {
  const minutes = Math.round(ms / 60_000)
  if (minutes < 1) return m.durSeconds(Math.max(Math.round(ms / 1000), 1))
  if (minutes < 60) return m.durMinutes(minutes)
  const h = Math.floor(minutes / 60)
  const rem = minutes % 60
  return rem > 0 ? m.durHoursMinutes(h, rem) : m.durHours(h)
}

/** Human file-kind: label for the meta line, badge text + tone for the tile. */
function fileKind(mimetype: string, name: string, m: Messages['run']): { label: string; badge: string; tone: string } {
  const ext = (name.split('.').pop() ?? '').toLowerCase()
  const mt = mimetype.toLowerCase()
  if (mt.includes('pdf') || ext === 'pdf') return { label: m.fileKind.pdf, badge: 'PDF', tone: 'pdf' }
  if (mt.includes('wordprocessingml') || ext === 'docx' || ext === 'doc')
    return { label: m.fileKind.word, badge: 'DOC', tone: 'doc' }
  if (mt.includes('spreadsheetml') || ext === 'xlsx' || ext === 'xls')
    return { label: m.fileKind.excel, badge: 'XLS', tone: 'sheet' }
  if (mt.includes('presentationml') || ext === 'pptx')
    return { label: m.fileKind.presentation, badge: 'PPT', tone: 'doc' }
  if (mt.includes('zip') || ext === 'zip') return { label: m.fileKind.zip, badge: 'ZIP', tone: 'archive' }
  if (mt.startsWith('image/')) return { label: m.fileKind.image, badge: 'IMG', tone: 'image' }
  if (mt.includes('csv') || ext === 'csv') return { label: m.fileKind.csv, badge: 'CSV', tone: 'sheet' }
  if (mt.includes('json') || ext === 'json') return { label: m.fileKind.json, badge: '{ }', tone: 'other' }
  if (ext === 'md' || mt.includes('markdown')) return { label: m.fileKind.markdown, badge: 'MD', tone: 'other' }
  if (mt.startsWith('text/') || ext === 'txt') return { label: m.fileKind.text, badge: 'TXT', tone: 'other' }
  return {
    label: m.fileKind.generic,
    badge: ext ? ext.slice(0, 4).toUpperCase() : 'FILE',
    tone: 'other'
  }
}
